// -- Tracking IDs -------------------------------------------------------------
// Replace with real IDs before going live.
export const GA4_ID            = 'G-41RR9FMSJL';      // Google Analytics 4 Measurement ID
export const META_PIXEL_ID     = '';                   // Facebook Pixel ID, e.g. '1234567890'
export const GADS_CONVERSION_ID = '';                  // Google Ads Conversion ID, e.g. 'AW-123456789'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
    gtag: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

// -- Helpers -------------------------------------------------------------------

function currentPage(): { page_url: string; page_title: string } {
  return {
    page_url:   typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
    page_title: typeof document !== 'undefined' ? document.title : '',
  };
}

// -- GA4 initialisation --------------------------------------------------------
export function initGA4() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.dataLayer as any[]).push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA4_ID, {
    send_page_view: false,        // fired manually on each route change
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
  });
}

// -- Page view -----------------------------------------------------------------
export function trackPageView(path: string, title?: string) {
  if (typeof window?.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path:     path,
    page_title:    title ?? document.title,
    page_location: window.location.href,
  });
}

// -- Generic event -------------------------------------------------------------
export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window?.gtag !== 'function') return;
  window.gtag('event', eventName, params);
}

// -- Scroll depth --------------------------------------------------------------
export function trackScrollDepth(depth: 25 | 50 | 75 | 90, path: string) {
  trackEvent('scroll_depth', {
    depth_percentage: depth,
    page_path: path,
  });
}

// -- WhatsApp click ------------------------------------------------------------
export function trackWhatsAppClick(source: string, service?: string) {
  const page = currentPage();
  trackEvent('whatsapp_click', {
    source,
    service_category: service ?? 'general',
    ...page,
  });

  // Meta Pixel Contact
  window.fbq?.('track', 'Contact', { content_name: source });

  // Google Ads conversion
  if (GADS_CONVERSION_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', { send_to: `${GADS_CONVERSION_ID}/whatsapp_click` });
  }
}

// -- Phone click ---------------------------------------------------------------
export function trackPhoneClick(source?: string) {
  const page = currentPage();
  trackEvent('phone_click', {
    source: source ?? 'unknown',
    ...page,
  });
  if (GADS_CONVERSION_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', { send_to: `${GADS_CONVERSION_ID}/phone_click` });
  }
}

// -- Email click ---------------------------------------------------------------
export function trackEmailClick(source?: string) {
  const page = currentPage();
  trackEvent('email_click', {
    source: source ?? 'unknown',
    ...page,
  });
}

// -- Lead form submit ----------------------------------------------------------
export interface LeadPayload {
  name: string;
  phone: string;
  service: string;
  message?: string;
  formType: string;
  sourcePage: string;
  sourceTitle: string;
  timestamp: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export function trackFormSubmit(payload: LeadPayload) {
  trackEvent('generate_lead', {
    form_type:        payload.formType,
    service_category: payload.service,
    page_url:         payload.sourcePage,
    page_title:       payload.sourceTitle,
    utm_source:       payload.utm_source,
    utm_medium:       payload.utm_medium,
    utm_campaign:     payload.utm_campaign,
    value: 1,
    currency: 'AED',
  });

  // Meta Pixel Lead
  window.fbq?.('track', 'Lead', {
    content_name:     payload.service,
    content_category: payload.formType,
  });

  // Google Ads
  if (GADS_CONVERSION_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', { send_to: `${GADS_CONVERSION_ID}/form_submit` });
  }
}

// -- CTA button click ----------------------------------------------------------
export function trackCtaClick(opts: {
  ctaText: string;
  ctaLocation: string;
  service?: string;
}) {
  const page = currentPage();
  trackEvent('cta_click', {
    cta_text:     opts.ctaText,
    cta_location: opts.ctaLocation,
    service:      opts.service ?? 'general',
    ...page,
  });
}

// -- Visa page view ------------------------------------------------------------
export function trackVisaPageView(visaType: string) {
  const page = currentPage();
  trackEvent('visa_enquiry', {
    visa_type: visaType,
    ...page,
  });
  window.fbq?.('track', 'ViewContent', { content_name: visaType, content_category: 'visa' });
}

// -- Package enquiry -----------------------------------------------------------
export function trackPackageEnquiry(packageName: string) {
  trackEvent('package_enquiry', {
    content_type: 'package',
    item_id: packageName,
    ...currentPage(),
  });
  window.fbq?.('track', 'ViewContent', { content_name: packageName, content_category: 'package' });
}

// -- Package detail view -------------------------------------------------------
export function trackPackageDetailView(packageName: string) {
  trackEvent('package_detail_view', {
    package_name: packageName,
    ...currentPage(),
  });
  window.fbq?.('track', 'ViewContent', { content_name: packageName, content_category: 'tour_package' });
}

// -- Blog CTA click ------------------------------------------------------------
export function trackBlogCtaClick(articleSlug: string, ctaType: string) {
  trackEvent('blog_cta_click', {
    article_slug: articleSlug,
    cta_type:     ctaType,
    ...currentPage(),
  });
}

// -- Custom trip enquiry -------------------------------------------------------
export function trackCustomTripEnquiry(service?: string) {
  const page = currentPage();
  trackEvent('custom_trip_enquiry', {
    service: service ?? 'general',
    ...page,
  });
}

// -- Meta Pixel initialisation -------------------------------------------------
export function initMetaPixel() {
  if (!META_PIXEL_ID) return;
  const f = window;
  const b = document;
  const e = 'script';
  if (f.fbq) return;
  const t = (f.fbq = function (...args: unknown[]) {
    (t as unknown as { callMethod: (...a: unknown[]) => void }).callMethod
      ? (t as unknown as { callMethod: (...a: unknown[]) => void }).callMethod(...args)
      : (t as unknown as { queue: unknown[] }).queue.push(args);
  }) as unknown as typeof f.fbq;
  if (!f._fbq) f._fbq = t;
  (t as unknown as { push: typeof t; loaded: boolean; version: string; queue: unknown[] }).push = t;
  (t as unknown as { push: typeof t; loaded: boolean; version: string; queue: unknown[] }).loaded = true;
  (t as unknown as { push: typeof t; loaded: boolean; version: string; queue: unknown[] }).version = '2.0';
  (t as unknown as { push: typeof t; loaded: boolean; version: string; queue: unknown[] }).queue = [];
  const s = b.createElement(e) as HTMLScriptElement;
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = b.getElementsByTagName(e)[0];
  first.parentNode?.insertBefore(s, first);
  window.fbq?.('init', META_PIXEL_ID);
  window.fbq?.('track', 'PageView');
}

// -- Track Meta Pixel lead event -----------------------------------------------
export function trackMetaLead(service?: string) {
  if (!META_PIXEL_ID || typeof window.fbq !== 'function') return;
  window.fbq('track', 'Lead', { content_name: service });
}
